import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';

import { namedOperations } from '#src/__generated__/operations.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { formatGqlErrors } from '#src/lib/gql/index.ts';

import type {
  ClaimSo5ReferralRewardMutation,
  ClaimSo5ReferralRewardMutationVariables,
} from './__generated__/useCoreClaimReferralReward.graphql.ts';

const CLAIM_REFERRAL_REWARD_MUTATION = gql`
  mutation ClaimSo5ReferralRewardMutation($input: claimReferralRewardInput!) {
    claimReferralReward(input: $input) {
      referralReward {
        id
        shippingState
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ClaimSo5ReferralRewardMutation,
  ClaimSo5ReferralRewardMutationVariables
>;

export const useCoreClaimReferralReward = () => {
  const [claim] = useMutation(CLAIM_REFERRAL_REWARD_MUTATION);
  const { showNotification } = useSnackNotificationContext();

  return useCallback(
    async (referralRewardId: string) => {
      const result = await claim({
        variables: {
          input: {
            referralRewardId,
          },
        },
        refetchQueries: [namedOperations.Query.ConfigQuery],
      });

      const errors = result.data?.claimReferralReward?.errors || [];

      if (errors.length) {
        showNotification('errors', { errors: formatGqlErrors(errors) });
        return errors;
      }
      return null;
    },
    [claim, showNotification]
  );
};
